import React, { useState, useEffect } from "react";
import {
  Box,
  Image,
  Text,
  VStack,
  HStack,
  IconButton,
  Spacer,
  useColorMode,
  Input,
  InputGroup,
  InputRightElement,
  Icon,
  useToast,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  SimpleGrid,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { useHistory } from "react-router-dom";
import { FaTrash } from "react-icons/fa";
import {
  CloseIcon,
  AddIcon,
  ArrowForwardIcon,
  ChevronDownIcon,
} from "@chakra-ui/icons";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import axiosInstance from "../../helpers/axiosInstance"; // Import axios instance
import GridIcon from "../../assets/icons/dot_grid_icon.svg"; // Import the grid icon SVG
import Translate from "../../components/LanguageTranslation/Translate";
import ExternalLinkButton from "../ExternalLinkButton";
import AddActivitiesModal from "./AddActivitiesModal";

import { set } from "lodash";
import { use } from "react";

const ActivitiesList = ({
  places,
  setPlaces,
  onActivityClick,
  tripId, // Pass tripId for API requests
  selectedDayIndex, // Index of the current day
  itinerary, // Full itinerary object (trip_plan)
  tripPlan, // City-specific trip_plan
  setItinerary, // Setter for the full itinerary (trip_plan)
  setView,
  handleSuggestionClick,
  isAddActivitiesOpen,
  setIsAddActivitiesOpen,
  city, // <-- Accept city as a prop
}) => {
  // console.log("ActivitiesList", places);
  const { colorMode } = useColorMode();
  const history = useHistory();
  const toast = useToast();
  const [hoveredItem, setHoveredItem] = useState(null);
  const [activityIndex, setActivityIndex] = useState(null);
  const [isDayModalOpen, setIsDayModalOpen] = useState(false);
  const [days, setDays] = useState([]);

  // Use tripPlan if provided, otherwise fallback to itinerary
  useEffect(() => {
    if (!places || !Array.isArray(places.itinerary)) {
      setDays([]); // Clear days if not available
      return;
    }
    // Map over the itinerary to get day names, fallback to "Day X"
    const daysArr = places.itinerary.map((day, idx) =>
      day?.current_day_name
        ? `Day ${idx + 1}`
        : `Day ${idx + 1}`
    );
    setDays(daysArr);
  }, [places]);

  // Helper to get the activities for the selected day
  const getDayActivities = () => {
    if (!places || !Array.isArray(places.itinerary)) return [];
    return places.itinerary[selectedDayIndex]?.activity_detail_list || [];
  };

  /* -------------------------------------------------------------- */
  /* Re-order activities inside the currently-selected day */
  const handleOnDragEnd = async (result) => {
    const { source, destination } = result;
    if (!destination) return;
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    )
      return; // nothing moved

    /* --------- 1.  local optimistic reorder --------- */
    const dayActs = getDayActivities();
    const newActs = [...dayActs];
    const [moved] = newActs.splice(source.index, 1);
    newActs.splice(destination.index, 0, moved);

    /* rebuild this city's `trip_plan` with new order */
    const newTripPlan = {
      ...places, // `places` is the city trip_plan object
      itinerary: places.itinerary.map((d, idx) =>
        idx === selectedDayIndex ? { ...d, activity_detail_list: newActs } : d
      ),
    };

    /* push up so parent <Itinerary> stays in-sync */
    setPlaces(newTripPlan);

    /* --------- 2.  persist to backend --------- */
    try {
      await axiosInstance.put(`/bucket-list/${tripId}/update/trip-plan`, {
        trip_plan: newTripPlan,
        city,
        day: selectedDayIndex + 1,
      });
      /* nothing else — UI is already correct */
    } catch (err) {
      console.error(err);
      toast({
        title: "Could not save new order.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      /* rollback – re-fetch from parent copy */
      setPlaces(places);
    }
  };
  /* -------------------------------------------------------------- */

  /* ------------------------------------------------------------------ */
  /* Delete a place from the currently-selected day */
  const handleActivityDelete = async (indexToRemove) => {
    const dayActs = getDayActivities();
    const newActs = dayActs.filter((_, i) => i !== indexToRemove);

    /* rebuild trip_plan just like in the drag handler */
    const newTripPlan = {
      ...places,
      itinerary: places.itinerary.map((d, idx) =>
        idx === selectedDayIndex ? { ...d, activity_detail_list: newActs } : d
      ),
    };

    /* optimistic UI */
    setPlaces(newTripPlan);

    try {
      await axiosInstance.put(`/bucket-list/${tripId}/update/trip-plan`, {
        trip_plan: newTripPlan,
        city,
        day: selectedDayIndex + 1,
      });
      toast({
        title: "Activity removed.",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (err) {
      console.error(err);
      toast({
        title: "Could not delete activity.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      /* rollback */
      setPlaces(places);
    }
  };
  /* ------------------------------------------------------------------ */

  const handleChangeDay = async (index) => {
    // Get the current day's activities
    const dayActs = getDayActivities();
    const updatedActivity = dayActs[activityIndex];
    const updatedCurrentDayActs = dayActs.filter((_, i) => i !== activityIndex);

    // Build new trip_plan for this city
    const newTripPlan = {
      ...places,
      itinerary: places.itinerary.map((d, idx) => {
        if (idx === selectedDayIndex) {
          // Remove from current day
          return { ...d, activity_detail_list: updatedCurrentDayActs };
        } else if (idx === index) {
          // Add to new day
          return {
            ...d,
            activity_detail_list: [
              ...(d.activity_detail_list || []),
              updatedActivity,
            ],
          };
        } else {
          return d;
        }
      }),
    };

    setPlaces(newTripPlan);

    // If setItinerary is available, update the full itinerary as well
    if (setItinerary && itinerary && itinerary.destinations) {
      const updatedItinerary = { ...itinerary };
      updatedItinerary.destinations = updatedItinerary.destinations.map(
        (dest) =>
          dest.city === city ? { ...dest, trip_plan: newTripPlan } : dest
      );
      setItinerary(updatedItinerary);
    }

    try {
      await axiosInstance.put(`/bucket-list/${tripId}/update/trip-plan`, {
        trip_plan: newTripPlan,
        city,
        day: index + 1,
      });
      setIsDayModalOpen(false);
    } catch (error) {
      toast({
        title: "Error moving activity",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCloseDetail = () => {
    setView(null);
  };

  return (
    <Box
      bg={colorMode === "dark" ? "gray.800" : "blackAlpha.50"}
      maxW={{ base: "100vw", md: "50vw", lg: "50vw" }}
      w={{ base: "100%", md: "100%", lg: "100%" }}
      h="100vh"
      overflowY="hidden"
    >
      <HStack justifyContent="space-between" bg="white">
        <Box
          flex="1"
          display="flex"
          justifyContent="left"
          p={4}
          pt={2}
          pb={2}
          pl={4}
          maxH="8vh"
          borderBottom="1px"
          borderLeft="1px"
          borderColor="blackAlpha.200"
        >
          <Text
            fontSize="md"
            fontWeight="bold"
            mt={1}
            pb={2}
            color="blackAlpha.700"
          >
            <Translate text="Update Activities" />
          </Text>
          <Spacer />
          <Menu>
            <MenuButton
              as={Button}
              bg="#1A202C"
              color="white"
              _hover={{ bg: "#1A202C" }}
              _active={{ bg: "#1A202C" }}
              size={{ base: "xs", md: "sm", lg: "sm" }}
              rightIcon={<ChevronDownIcon />}
              mr={4}
            >
              <Translate text="Update Activities" />
            </MenuButton>
            <MenuList>
              <MenuItem
                onClick={() => setIsAddActivitiesOpen(true)}
                fontSize={{ base: "xs", md: "sm", lg: "sm" }}
              >
                <Translate text="Add Bucket List Items" />
              </MenuItem>
              <MenuItem
                onClick={() =>
                  handleSuggestionClick(
                    `Could you give me more recommendation for activities in ${city}?`,
                    "explore_conversation"
                  )
                }
                fontSize={{ base: "xs", md: "sm", lg: "sm" }}
              >
                <Translate text="Get more Recommendations" />
              </MenuItem>
            </MenuList>
          </Menu>

          <IconButton
            icon={<CloseIcon boxSize={2.5} />}
            onClick={handleCloseDetail}
            colorScheme="gray"
            size="sm"
            isRound={true}
            position="relative"
            zIndex="tooltip"
            bg="white"
            boxShadow="md"
          />
        </Box>
      </HStack>
      <Box h={{ base: "91vh", md: "89vh", lg: "89vh" }}>
        <DragDropContext onDragEnd={handleOnDragEnd}>
          <Droppable droppableId="day-activities">
            {(provided) => (
              <VStack
                spacing={4}
                h="100%"
                overflowY="auto"
                sx={{
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                  msOverflowStyle: "none",
                  scrollbarWidth: "none",
                }}
                bg="white"
                align="stretch"
                p={4}
                m={2}
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {getDayActivities().map((place, index) => (
                  <Draggable
                    key={place.id || index}
                    draggableId={
                      place.id ? String(place.id) : `draggable-${index}`
                    }
                    index={index}
                  >
                    {(provided) => (
                      <Box
                        position="relative"
                        bg="white"
                        borderRadius="lg"
                        boxShadow="md"
                        border="1px solid"
                        borderColor="gray.200"
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        justifyContent="space-between"
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        onMouseEnter={() => setHoveredItem(index)}
                        onMouseLeave={() => setHoveredItem(null)}
                      >
                        {/* Numbering circle */}
                        <Box
                          position="absolute"
                          top="-10px"
                          left="-10px"
                          bg="black"
                          color="white"
                          borderRadius="full"
                          width="24px"
                          height="24px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          fontSize="12px"
                          fontWeight="bold"
                        >
                          {index + 1}
                        </Box>
                        <Box
                          {...provided.dragHandleProps}
                          bg="gray.100"
                          alignContent="center"
                          visibility={
                            hoveredItem === index ? "visible" : "hidden"
                          }
                          h="100%"
                        >
                          <Image
                            src={GridIcon}
                            alt="Drag Handle"
                            boxSize="4"
                            opacity={hoveredItem === index ? 1 : 0.5}
                          />
                        </Box>
                        <VStack ml={1} w="full" p={4} pl={0} h="100%">
                          <HStack align="center" spacing={4} w="100%">
                            <VStack
                              align="flex-start"
                              spacing={1}
                              flex="1"
                              h="100%"
                            >
                              <Text fontSize="18px" fontWeight="bold" mb={0}>
                                <Translate text={place.title} />
                              </Text>
                              <Text fontSize="14px" color="gray.600" mb={0}>
                                <Translate text="Rating:" />{" "}
                                {place.reviews?.combinedAverageRating.toFixed(2)
                                  ? `${place.reviews?.combinedAverageRating.toFixed(
                                      2
                                    )} ★`
                                  : "Not Available"}
                              </Text>
                            </VStack>
                            {place.images && place.images[0] && place.images[0].variants && place.images[0].variants[0] && place.images[0].variants[2].url && (
                              <Image
                                src={place.images[0].variants[2].url}
                                alt={place.title}
                                boxSize="120px"
                                borderRadius="lg"
                                objectFit="cover"
                              />
                            )}
                          </HStack>
                          <HStack spacing={1} w="100%">
                            {place.productUrl && (
                              <ExternalLinkButton
                                as="a"
                                href={place.productUrl}
                                rel="noopener noreferrer"
                                size="sm"
                                bg="#286328"
                                _hover="none"
                                _active="none"
                                textColor="white"
                                mr={2}
                              >
                                <Translate text="Open" />
                              </ExternalLinkButton>
                            )}
                            <Button
                              onClick={() => {
                                setIsDayModalOpen(true);
                                setActivityIndex(index);
                              }}
                              target="_blank"
                              rel="noopener noreferrer"
                              size="sm"
                            >
                              <Translate text="Change Day" />
                            </Button>
                          </HStack>

                          <HStack spacing={1} w="100%">
                            <Spacer />

                            <IconButton
                              icon={<FaTrash />}
                              aria-label="Delete place"
                              borderRadius="full"
                              size="xs"
                              visibility={
                                hoveredItem === index ? "visible" : "hidden"
                              }
                              onClick={() => handleActivityDelete(index)}
                            />
                          </HStack>
                        </VStack>
                      </Box>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </VStack>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
      <Modal isOpen={isDayModalOpen} onClose={() => setIsDayModalOpen(false)}>
        <ModalOverlay />
        <ModalContent p={4}>
          <ModalHeader>Select a Day</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <SimpleGrid columns={3} spacing={4}>
              {days.map((day, index) => (
                <Box
                  key={index}
                  p={4}
                  borderWidth="1px"
                  borderRadius="md"
                  textAlign="center"
                  cursor="pointer"
                  bg={selectedDayIndex === index ? "#286328" : "gray.100"}
                  color={selectedDayIndex === index ? "white" : "black"}
                  onClick={() => handleChangeDay(index)}
                  noOfLines={2}
                >
                  {day}
                </Box>
              ))}
            </SimpleGrid>
          </ModalBody>
        </ModalContent>
      </Modal>
      <AddActivitiesModal
        isOpen={isAddActivitiesOpen}
        onClose={() => setIsAddActivitiesOpen(false)}
        tripId={tripId}
        places={places}
        setPlaces={setPlaces}
        selectedDayIndex={selectedDayIndex}
        city={city}
      />
    </Box>
  );
};

export default ActivitiesList;
